import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent } from '../../Components/UI/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../Components/UI/table';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Avatar, AvatarFallback } from '../../Components/UI/avatar';
import { Plus, Pencil, ArrowUp, ArrowDown, ShieldCheck, User, UserRound, ArrowLeftRight, KeySquare, Search, X, Crown } from 'lucide-react';
import ContactActions from '../../Components/Common/ContactActions';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import UserEntry from './UserEntry';
import { userIsAdmin, userIsCreator, getUsersArr, getAgencyDetails } from '../../Store/Reducers';
import ChangeAdmin from './SystemChange/ChangeAdmin/ChangeAdmin';
import TransferAssignments from './TransferAssignments/TransferAssignments';

const Users = () => {
  const users = useSelector(getUsersArr);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [dialogStates, setDialogStates] = useState({
    userEntry: false,
    changeAdmin: false,
    transferAssignment: false
  });
  const [currentUser, setCurrentUser] = useState({UserID: 0});
  // Table header configuration
  const tableHeaderCols = [
    { key: 'Name', label: 'Name', className: 'justify-start min-w-[220px]' },
    { key: 'Role', label: 'Role', className: 'justify-start' },
    { key: 'Designation', label: 'Designation', className: 'justify-start' },
    { key: 'Mobile', label: 'Mobile', className: 'justify-start' },
    { key: 'Email', label: 'Email', className: 'justify-start' },
  ];
  const isAdmin = useSelector(userIsAdmin);
  const isCreatorUser = useSelector(userIsCreator); // Change Admin transfers Agencies.AdminUserID - creator-only
  const agencyData = useSelector(getAgencyDetails);
  const shouldShowChangeAdminButton = users && users.length > 1 && isCreatorUser;

  // Helper for Status Priority: Current users (0) -> Pending users (1) -> Declined users (2)
  const getStatusPriority = (user) => {
    if (user.Status === 0 || user.Status === "0") return 1; // Pending
    if (user.Status === 2 || user.Status === "2") return 2; // Declined
    return 0; // Current / Active
  };

  // Helper for Role Priority: Creator (0) -> Admin (1) -> Normal User (2)
  const getRolePriority = (user) => {
    if (agencyData?.AdminUserID === user.UserID) return 0; // Creator
    if (user.IsAdmin && user.FullAccess) return 1; // Admin
    return 2; // Normal User
  };

  // Default 3-tier sort comparator: Status -> Role -> Name
  const defaultSort = (a, b) => {
    // Priority 1: Current -> Pending -> Declined
    const statusA = getStatusPriority(a);
    const statusB = getStatusPriority(b);
    if (statusA !== statusB) return statusA - statusB;

    // Priority 2: Creator -> Admin -> Normal User
    const roleA = getRolePriority(a);
    const roleB = getRolePriority(b);
    if (roleA !== roleB) return roleA - roleB;

    // Priority 3: Name alphabetically
    const nameA = a.Name?.toLowerCase() || '';
    const nameB = b.Name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  };

  // Sort functionality
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Function which will return filtered and sorted users
  const getSortedUsers = () => {
    let list = users || [];

    // Filter by search term across Name, Mobile, and Email
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter((user) => {
        const name = (user.Name || '').toLowerCase();
        const email = (user.Email || '').toLowerCase();
        const mobile = (user.Mobile || '').toString().toLowerCase();
        const cc = (user.CC || '').toString().toLowerCase();
        const fullMobile = `${cc}${mobile}`;
        const plusMobile = `+${cc} ${mobile}`.toLowerCase();
        const designation = (user.Designation || '').toLowerCase();

        return (
          name.includes(term) ||
          email.includes(term) ||
          mobile.includes(term) ||
          fullMobile.includes(term) ||
          plusMobile.includes(term) ||
          designation.includes(term)
        );
      });
    }

    if (!sortConfig.key) {
      return [...list].sort(defaultSort);
    }

    return [...list].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Handle specific sorting cases
      switch (sortConfig.key) {
        case 'Name':
          aValue = a.Name?.toLowerCase() || '';
          bValue = b.Name?.toLowerCase() || '';
          break;
        case 'Role':
          aValue = getRolePriority(a);
          bValue = getRolePriority(b);
          break;
        case 'Designation':
          aValue = a.Designation?.toLowerCase() || '';
          bValue = b.Designation?.toLowerCase() || '';
          break;
        case 'Mobile':
          aValue = a.Mobile || '';
          bValue = b.Mobile || '';
          break;
        case 'Email':
          aValue = a.Email?.toLowerCase() || '';
          bValue = b.Email?.toLowerCase() || '';
          break;
        default:
          break;
      }

      if (aValue !== bValue) {
        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }

      return defaultSort(a, b);
    });
  };

  // Function which will handle click on user
  const handleRowClick = (user) => {
    setCurrentUser(user);
    setDialogStates((prev) => ({ ...prev, userEntry: true }))
  };

  // Function which will handle edit user
  const handleEditUser = (e, user) => {
    e.stopPropagation();
    setCurrentUser(user);
    setDialogStates((prev) => ({ ...prev, userEntry: true }))
  };
  
  // Function which will handle transfer connections or threads
  const handletransferAssignment = (e, user) => {
    e.stopPropagation();
    setCurrentUser(user);
    setDialogStates((prev) => ({ ...prev, transferAssignment: true }))
  };

  // Function which will return user role
  const getUserRole = (user) => {
    if (agencyData?.AdminUserID === user.UserID) {
      return { label: 'Creator', icon: Crown };
    } else if (user.IsAdmin && user.FullAccess) {
      return { label: 'Admin', icon: ShieldCheck };
    } else {
      return { label: 'User', icon: User };
    }
  };

  // Function which will return user status badge
  const getUserStatus = (user) => {
    if (user.Status === 0 || user.Status === "0") {
      return { label: 'Pending', className: 'bg-gray-100 text-gray-800 border-gray-400' };
    }
    if (user.Status === 2 || user.Status === "2") {
      return { label: 'Declined', className: 'bg-red-50 text-red-700 border-red-200' };
    }
    return null;
  };

  // Function which will return avatar fallback class based on status
  const getAvatarBgClass = (user) => {
    if (user.Status === 0 || user.Status === "0") {
      return 'bg-white text-gray-600 border border-dashed border-gray-600';
    }
    if (user.Status === 2 || user.Status === "2") {
      return 'bg-white text-sahi-red border border-dashed border-sahi-red';
    }
    return 'bg-sahi-dark-blue text-sahi-white';
  };

  // Function which will format mobile number
  const formatMobile = (cc, mobile) => {
    return `+${cc} ${mobile}`;
  };

  const sortedUsers = getSortedUsers();

  // Desktop table component
  const DesktopTable = () => {
    return (
      <>
        <Card className="bg-sahi-white hidden lg:block !pt-0 overflow-hidden">
          <CardContent className="!px-0 !sahi-radius">
            <Table className="min-w-full table-auto">
              <TableHeader>
                <TableRow className="bg-sahi-white py-1.5 h-11">
                  {tableHeaderCols.map(({ key, label, className = '' }) => (
                    <TableHead
                      key={key}
                      className={`table-header-text word-break-px ${className}`}
                      onClick={() => sortData(key)}
                    >
                      <div className={`flex items-center cursor-pointer break-words`}>
                        {label}
                        {sortConfig.key === key && sortConfig.direction && (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="ml-1 h-4 w-4 text-black" />
                          ) : (
                            <ArrowDown className="ml-1 h-4 w-4 text-black" />
                          )
                        )}
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="table-header-text whitespace-nowrap text-left pl-4">
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-gray-500 text-sm">
                      {searchTerm ? `No users found matching "${searchTerm}".` : 'No users available.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedUsers.map((user, index) => {
                  const role = getUserRole(user);
                  const RoleIcon = role.icon;
                  const statusInfo = getUserStatus(user);

                  return (
                    <TableRow
                      key={user.UserID || index}
                      onClick={() => handleRowClick(user)}
                      className="hover:bg-gray-50 cursor-pointer h-8"
                    >
                      {/* Name */}
                      <TableCell className="uniform-spacing pl-4 !align-middle">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarFallback className={getAvatarBgClass(user)}>
                              <UserRound className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-text-color">
                              {user.Name || '-'}
                            </span>
                            {statusInfo && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusInfo.className}`}>
                                {statusInfo.label}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Role */}
                      <TableCell className="uniform-spacing pl-4 !align-middle">
                        <div className="flex items-center gap-2">
                          <RoleIcon className="h-4 w-4 text-sahi-primary-color" />
                          <span className="text-sahi-primary-color font-medium">
                            {role.label}
                          </span>
                        </div>
                      </TableCell>

                      {/* Designation */}
                      <TableCell className="uniform-spacing pl-4 !align-middle">
                        <span className="text-text-color">
                          {user.Designation || '-'}
                        </span>
                      </TableCell>

                      {/* Mobile */}
                      <TableCell className="uniform-spacing pl-4 !align-middle">
                        <span className="text-text-color">
                          {user.Mobile ? formatMobile(user.CC, user.Mobile) : '-'}
                        </span>
                      </TableCell>

                      {/* Email */}
                      <TableCell className="uniform-spacing pl-4 !align-middle">
                        <span className="text-text-color">
                          {user.Email || '-'}
                        </span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="uniform-spacing pr-4 !align-middle">
                        <div className="flex gap-2 items-center justify-end">
                          {/* Phone */}
                          <ContactActions
                            connectionData={{
                              mobile: user?.Mobile || "",
                              email: user?.Email || "",
                              cc: user?.CC || "91"
                            }}
                            showDashes={false}
                          />
                          {/* Edit */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sahi-blue action-btn-effect hover:bg-sahi-blue-light"
                            onClick={(e) => handleEditUser(e, user)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </>
    );
  };

  // Mobile card component
  const MobileCard = ({ user }) => {
    const role = getUserRole(user);
    const RoleIcon = role.icon;
    const statusInfo = getUserStatus(user);

    return (
      <div
        onClick={() => handleRowClick(user)}
        className="bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full f-col justify-between 
          cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className="f-row justify-between mobile-tile-header-layout mb-1.5 p-4">
          <div className="f-col justify-between gap-1 items-start min-w-0 flex-1">
            <div className="f-row items-center gap-3 w-full">
              <Avatar className="h-10 w-10 flex-shrink-0">
                <AvatarFallback className={getAvatarBgClass(user)}>
                  <UserRound className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div className="f-col min-w-0 gap-1 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-text-color break-words text-sm">
                    {user.Name || "-"}
                  </span>
                  {statusInfo && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusInfo.className}`}>
                      {statusInfo.label}
                    </span>
                  )}
                </div>
                <span className="text-xs text-sub-text-color truncate">
                  {user.Designation || ""}
                </span>
              </div>
            </div>
          </div>

          <div className="f-col justify-between gap-1 text-right flex-shrink-0 ml-2">
            <div className="f-col items-end text-right">
              <div className='f-row justify-end gap-2 items-center'>
                {user.Mobile && (
                  <span className="text-xs text-sub-text-color">
                    {formatMobile(user.CC, user.Mobile)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with actions and details */}
        <div className="flex h-[38px] justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout px-4">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="flex items-center gap-1 text-xs text-sahi-primary-color" >
              <RoleIcon className="h-3 w-3" />
              {role.label}
            </span>
          </div>

          <div className="flex items-center gap-4 justify-end">
            <ContactActions
              connectionData={{
                mobile: user?.Mobile || "",
                email: user?.Email || "",
                cc: user?.CC || "91"
              }}
              showDashes={false}
              showEmail={false}
            />

            <Button
              variant="ghost"
              onClick={(e) => handleEditUser(e, user)}
              className="h-6 w-6 p-0 flex items-center justify-center text-sahi-blue hover:bg-sahi-blue-light hover:text-sahi-blue rounded-md active:scale-95 transition-all duration-200"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header with Search and Add User Button */}
        <div className="hidden sm:flex justify-between items-center">
          <h1 className="text-3xl font-bold text-text-color mr-4 web-header-name">Users</h1>
          <div className="flex items-center gap-3">
            <div className="relative w-72 md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-8 h-10 w-full bg-white border-gray-300 focus:border-sahi-green focus:ring-sahi-green text-sm"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {isAdmin && (
              <Button
                className="button-config text-base sahi-primary-button flex items-center shrink-0"
                onClick={() => {
                  setCurrentUser(null);
                  setDialogStates((prev) => ({ ...prev, userEntry: true }));
                }}
              >
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Table */}
        <DesktopTable />

        {/* Change Admin button for desktop - within the content area */}
        <div className='hidden lg:block lg:flex lg:justify-start lg:gap-4 lg:items-center lg:mt-2'>
          {isAdmin && <Button
            className="button-config text-base sahi-primary-button flex items-center"
            onClick={() => {
              setCurrentUser(null);
              setDialogStates((prev) => ({ ...prev, userEntry: true }))
            }}
          >
            <Plus className="h-4 w-4" />
            Add User
        </Button>}
        {shouldShowChangeAdminButton && (
          <div className="hidden lg:block">
            <Button
              onClick={() => setDialogStates((prev) => ({ ...prev, changeAdmin: true }))}
              className="button-config text-base sahi-primary-button flex items-center"
            >
              <KeySquare className="h-4 w-4" />
              Change Admin
            </Button>
          </div>
        )}
        </div>

        {/* Mobile Search Bar */}
        <div className="sm:hidden w-full relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search users"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-8 h-10 w-full bg-white border-gray-300 focus:border-sahi-green focus:ring-sahi-green text-sm shadow-sm"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Mobile View - Cards */}
        <div className="lg:hidden space-y-2">
          {sortedUsers.length === 0 ? (
            <div className="text-center py-10 bg-white sahi-radius border border-gray-200 text-gray-500 text-sm">
              {searchTerm ? `No users found matching "${searchTerm}".` : 'No users available.'}
            </div>
          ) : (
            sortedUsers.map((user, index) => (
              <MobileCard key={user.UserID || index} user={user} />
            ))
          )}
        </div>
        <div className="lg:hidden">
            <Button
              onClick={() => {
                setCurrentUser(null);
                setDialogStates((prev) => ({ ...prev, userEntry: true }))
              }}
              className="text-base flex items-center w-full"
            >
              <Plus className="h-4 w-4" />
              Add User
            </Button>

            {shouldShowChangeAdminButton && (
            <Button
              onClick={() => setDialogStates((prev) => ({ ...prev, changeAdmin: true }))}
              className="text-base flex items-center w-full mt-2"
            >
              Change Admin
            </Button>
          )}
          </div>
      </div>

      {/* User entry dialog */}
      <UserEntry
        isOpen={dialogStates.userEntry}
        setIsOpen={(open) => setDialogStates((prev) => ({ ...prev, userEntry: open }))}
        userData={currentUser}
      />

      {/* Change Admin dialog */}
      <ChangeAdmin
        isOpen={dialogStates.changeAdmin}
        setIsOpen={(open) => setDialogStates((prev) => ({ ...prev, changeAdmin: open }))}
      />

      {/* Transfer Assignment dialog */}
      {users.length != 0 && <TransferAssignments
        isOpen={dialogStates.transferAssignment}
        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, transferAssignment: open }))}
        user={currentUser}
      />}

    </TooltipProvider>
  );
};

export default Users